'use client';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import posthog from 'posthog-js';
import { AnimatePresence } from 'framer-motion';
import { useUser } from '@clerk/nextjs';
import { Plus, Wallet, PieChart, TrendingUp, Trash2, Search, RefreshCw, Eye, LayoutGrid, Columns, Upload, ChevronDown, AlertTriangle, X } from 'lucide-react';
import { Stock, StockLot, PortfolioSummary, StockSearchResult } from '@/types';
import { fetchStockData, fetchExchangeRate, searchStocks, findPriceForDate, fetchHistoricalPrice } from '@/lib/stockService';
import { loadPortfolio, savePortfolio, migrateLocalPortfolio, mergePortfolios } from '@/lib/sync';
import { useCurrency } from '@/components/CurrencyContext';
import { useSortBy } from '@/components/SortContext';
import { useStockListView } from '@/components/StockListViewContext';
import { useSubscription } from '@/components/SubscriptionContext';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { StockCard } from '@/components/StockCard';
import { StockDetail } from '@/components/StockDetail';
import { PortfolioChart } from '@/components/PortfolioChart';
import { UpgradeModal } from '@/components/UpgradeModal';
import { BulkUploadModal } from '@/components/BulkUploadModal';
import { FREE_TIER_ASSET_LIMIT } from '@/types/subscription';
import { formatNumber } from '@/lib/formatNumber';
import Link from 'next/link';

// Helper to generate IDs
const generateId = () => Math.random().toString(36).substr(2, 9);

type Currency = 'USD' | 'CAD';

interface NewLot {
  id: string;
  shares: string;
  price: string;
  currency: Currency;
  purchasedDate: string; // YYYY-MM-DD, optional
}

export default function HomePage() {
  const { isSignedIn, user } = useUser();
  const { currency } = useCurrency();
  const { sortBy } = useSortBy();
  const { tier } = useSubscription();
  const { stockListView, setStockListView } = useStockListView();
  const [stocks, setStocks] = useState<Record<string, Stock>>({});
  const [isMounted, setIsMounted] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(56);

  // Measure actual header height for sticky toolbar offset, re-measure on resize
  useEffect(() => {
    const header = document.querySelector('header');
    if (!header) return;
    setHeaderHeight(header.offsetHeight);
    const observer = new ResizeObserver(() => {
      setHeaderHeight(header.offsetHeight);
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  // Derived array for rendering
  const stocksArray = useMemo(() => {
    const arr = Object.values(stocks);
    if (sortBy === 'alphabetical') {
      return arr.sort((a, b) => a.symbol.localeCompare(b.symbol));
    }
    if (sortBy === 'alphabeticalByName') {
      return arr.sort((a, b) => (a.name ?? a.symbol).localeCompare(b.name ?? b.symbol));
    }
    // lastUpdated: most recently modified by user first, nulls at end
    return arr.sort((a, b) => {
      if (!a.userModifiedAt && !b.userModifiedAt) return 0;
      if (!a.userModifiedAt) return 1;
      if (!b.userModifiedAt) return -1;
      return new Date(b.userModifiedAt).getTime() - new Date(a.userModifiedAt).getTime();
    });
  }, [stocks, sortBy]);

  const [selectedStockId, setSelectedStockId] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<number>(1.35);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasMigrated, setHasMigrated] = useState(false);
  const [hasInitialSyncCompleted, setHasInitialSyncCompleted] = useState(false);
  const [cloudSaveError, setCloudSaveError] = useState<string | null>(null);

  // Close add menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
        setIsAddMenuOpen(false);
      }
    };
    if (isAddMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isAddMenuOpen]);

  // Load from localStorage after mount (client-side only)
  useEffect(() => {
    setIsMounted(true);
    try {
      const saved = localStorage.getItem('folioTrack_stocks');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Handle both legacy array format and new map format
        if (Array.isArray(parsed)) {
          // Legacy array format - convert to map
          const migrated: Record<string, Stock> = {};
          for (const s of parsed) {
            migrated[s.id] = { ...s, currency: s.currency || 'USD', isLoading: false };
          }
          setStocks(migrated);
        } else {
          // New map format - ensure currency exists
          const migrated: Record<string, Stock> = {};
          for (const [id, s] of Object.entries(parsed as Record<string, Stock>)) {
            migrated[id] = { ...s, currency: s.currency || 'USD', isLoading: false };
          }
          setStocks(migrated);
        }
      }
    } catch (error) {
      console.error('Failed to load stocks from localStorage:', error);
    }
  }, []);

  // Add Stock Form State
  const [newSymbol, setNewSymbol] = useState('');
  const [selectedExchange, setSelectedExchange] = useState('');

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<StockSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lotsScrollRef = useRef<HTMLDivElement>(null);

  const [newLots, setNewLots] = useState<NewLot[]>([
    { id: generateId(), shares: '', price: '', currency: 'USD', purchasedDate: '' }
  ]);
  const [isAddingStock, setIsAddingStock] = useState(false);
  const [addError, setAddError] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isWatchlistOnly, setIsWatchlistOnly] = useState(false);
  const [activeListTab, setActiveListTab] = useState<'portfolio' | 'watchlist'>('portfolio');
  const [portfolioSearch, setPortfolioSearch] = useState('');

  // Persist to local storage immediately
  useEffect(() => {
    const payload = JSON.stringify(stocks);
    try {
      localStorage.setItem('folioTrack_stocks', payload);
    } catch (err) {
      // OHLC history can blow past the ~5MB localStorage quota for users with
      // many stocks. Drop historicalData (it's recomputed on load) and retry.
      const slim: typeof stocks = {};
      for (const [id, s] of Object.entries(stocks)) {
        slim[id] = { ...s, historicalData: [] };
      }
      try {
        localStorage.setItem('folioTrack_stocks', JSON.stringify(slim));
      } catch (innerErr) {
        console.error('Failed to persist stocks to localStorage:', innerErr);
      }
      console.warn('localStorage quota exceeded; persisted stocks without history.', err);
    }
  }, [stocks]);

  // Fetch Exchange Rate on Mount
  useEffect(() => {
    const getRate = async () => {
      const rate = await fetchExchangeRate();
      setExchangeRate(rate);
    };
    getRate();
  }, []);

  // Sync logic: Load from cloud on sign-in
  const hasAutoRefreshedRef = useRef(false);

  useEffect(() => {
    if (!isSignedIn || !user) return;

    const syncFromCloud = async () => {
      setIsSyncing(true);
      try {
        const cloudStocks = await loadPortfolio();

        if (cloudStocks === null || Object.keys(cloudStocks).length === 0) {
          // No cloud portfolio found, migrate localStorage data
          const localStocks = stocks;
          if (Object.keys(localStocks).length > 0 && !hasMigrated) {
            const success = await migrateLocalPortfolio(user.id, localStocks);
            if (success) {
              setHasMigrated(true);
              console.log('Portfolio migrated to cloud successfully');
            }
          }
        } else {
          // Cloud portfolio exists, use it
          const merged = mergePortfolios(cloudStocks, stocks);
          setStocks(merged);
          // Reset refresh flag when syncing new data from cloud
          hasAutoRefreshedRef.current = false;
        }
      } catch (error) {
        console.error('Failed to sync from cloud:', error);
      } finally {
        setIsSyncing(false);
        setHasInitialSyncCompleted(true);
      }
    };

    syncFromCloud();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user?.id]);

  // Debounced save to cloud when signed in
  useEffect(() => {
    if (!isSignedIn || !user || isSyncing || !hasInitialSyncCompleted) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save to cloud (500ms)
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await savePortfolio(user.id, stocks);
        setCloudSaveError(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save to cloud';
        console.error('Failed to save to cloud:', error);
        setCloudSaveError(message);
        posthog.captureException(error instanceof Error ? error : new Error(message));
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [stocks, isSignedIn, user, isSyncing, hasInitialSyncCompleted]);

  // Auto-refresh stock data on mount or when stocks are loaded
  useEffect(() => {
    // Only refresh once after initial sync is complete
    if (!isMounted || stocksArray.length === 0 || !hasInitialSyncCompleted || hasAutoRefreshedRef.current || isSyncing) return;

    // Check if any stock needs a refresh (data is stale, missing, or has no historical data)
    const needsRefresh = stocksArray.some(stock => {
      // Always refresh if no historical data (happens when loading from cloud without localStorage)
      if (!stock.historicalData || stock.historicalData.length === 0) return true;
      // Refresh if cached history predates OHLC support (no candle fields on any point),
      // otherwise the candlestick toggle stays permanently disabled until the 1h staleness
      // refresh kicks in.
      const hasOhlc = stock.historicalData.some(p => p.open != null && p.high != null && p.low != null && p.close != null);
      if (!hasOhlc) return true;
      // Refresh if never updated or data is older than 1 hour
      if (!stock.lastUpdated) return true;
      const lastUpdate = new Date(stock.lastUpdated);
      const hoursSinceUpdate = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60);
      return hoursSinceUpdate > 1;
    });

    if (needsRefresh) {
      hasAutoRefreshedRef.current = true; // Mark as refreshed to prevent repeated refreshes

      const refreshAllStocks = async () => {
        const promises = stocksArray.map(async (stock) => {
          try {
            const data = await fetchStockData(stock.symbol);
            return {
              ...stock,
              currentPrice: data.currentPrice,
              name: data.name,
              exchange: data.exchange,
              currency: data.currency,
              historicalData: data.history,
              groundingUrls: data.groundingUrls,
              marketCap: data.marketCap,
              nextEarningsDate: data.nextEarningsDate,
              instrumentType: data.instrumentType,
              sector: stock.sector ?? data.sector,
              lastUpdated: new Date().toISOString(),
              isLoading: false,
            };
          } catch (error) {
            console.error(`Failed to refresh ${stock.symbol}:`, error);
            return stock; // Keep original data if refresh fails
          }
        });

        const refreshedStocks = await Promise.all(promises);
        // Merge refreshed data into current state, preserving any stocks added during refresh
        setStocks(prev => {
          const updated = { ...prev };
          for (const stock of refreshedStocks) {
            if (stock.id in updated) {
              updated[stock.id] = stock;
            }
          }
          return updated;
        });
      };

      refreshAllStocks();
    }
  }, [isMounted, stocksArray, hasInitialSyncCompleted, isSyncing]);

  // Handle Search Input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear selection if user types
    if (newSymbol && query !== newSymbol) {
        setNewSymbol('');
        setSelectedExchange('');
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      const results = await searchStocks(query);
      setSearchResults(results);
      setIsSearching(false);
    }, 500);
  };

  const handleSelectStock = (result: StockSearchResult) => {
    setHasAttemptedSubmit(false);
    setNewSymbol(result.symbol);
    setSelectedExchange(result.exchange);
    setSearchQuery(`${result.symbol} - ${result.name}`);
    setSearchResults([]);

    // Auto-detect Canadian exchange to default lot currency
    const isCanadian = ['TOR', 'TSX', 'VAN', 'CNQ'].some(e =>
      result.exchange.toUpperCase().includes(e) || result.symbol.endsWith('.TO') || result.symbol.endsWith('.V')
    );

    const detectedCurrency = isCanadian ? 'CAD' : 'USD';
    setNewLots(prev => prev.map(l => ({ ...l, currency: detectedCurrency, purchasedDate: l.purchasedDate })));
  };

  const resetAddForm = () => {
    setNewSymbol('');
    setSelectedExchange('');
    setSearchQuery('');
    setSearchResults([]);
    setNewLots([{ id: generateId(), shares: '', price: '', currency: 'USD', purchasedDate: '' }]);
    setAddError('');
    setHasAttemptedSubmit(false);
    setIsWatchlistOnly(false);
  };

  // Currency Conversion Helper
  const convert = useCallback((amountUSD: number | null | undefined) => {
    if (amountUSD === null || amountUSD === undefined) return 0;
    return currency === 'CAD' ? amountUSD * exchangeRate : amountUSD;
  }, [currency, exchangeRate]);

  // Derived arrays for portfolio vs watchlist
  const portfolioStocks = useMemo(() => stocksArray.filter(s => !s.watchlist), [stocksArray]);
  const watchlistStocks = useMemo(() => stocksArray.filter(s => s.watchlist), [stocksArray]);

  // Filter by portfolio search (ticker symbol or company name, case-insensitive)
  const normalizedPortfolioSearch = portfolioSearch.trim().toLowerCase();
  const applyPortfolioSearch = useCallback((arr: Stock[]) => {
    if (!normalizedPortfolioSearch) return arr;
    return arr.filter(s =>
      s.symbol.toLowerCase().includes(normalizedPortfolioSearch) ||
      (s.name ?? '').toLowerCase().includes(normalizedPortfolioSearch)
    );
  }, [normalizedPortfolioSearch]);

  // Derived state for portfolio summary (excludes watchlist stocks)
  const summary: PortfolioSummary = portfolioStocks.reduce((acc, stock) => {
    const stockShares = stock.lots.reduce((s, l) => s + l.shares, 0);

    const stockCostUSD = stock.lots.reduce((c, l) => {
      const lotCost = l.shares * l.price;
      const lotCurrency = l.currency || 'USD';
      const costInUSD = lotCurrency === 'CAD' ? lotCost / exchangeRate : lotCost;
      return c + costInUSD;
    }, 0);

    const priceInUSD = (stock.currency === 'CAD' && stock.currentPrice)
      ? stock.currentPrice / exchangeRate
      : (stock.currentPrice || 0);

    const stockValueUSD = priceInUSD * stockShares;

    return {
      totalValue: acc.totalValue + stockValueUSD,
      totalCost: acc.totalCost + stockCostUSD,
      totalProfit: 0,
      totalProfitPercent: 0
    };
  }, { totalValue: 0, totalCost: 0, totalProfit: 0, totalProfitPercent: 0 });

  summary.totalProfit = summary.totalValue - summary.totalCost;
  summary.totalProfitPercent = summary.totalCost > 0 ? (summary.totalProfit / summary.totalCost) * 100 : 0;

  // Validation for new lots - check for negative or zero values
  const getInvalidLots = () => {
    return newLots.filter(lot => {
      const shares = parseFloat(lot.shares);
      const price = parseFloat(lot.price);
      return (lot.shares !== '' && (isNaN(shares) || shares <= 0)) ||
             (lot.price !== '' && (isNaN(price) || price <= 0));
    });
  };
  const invalidLotIds = new Set(getInvalidLots().map(l => l.id));
  const hasLotValidationError = invalidLotIds.size > 0;

  const handleAddLotRow = () => {
    setNewLots(prev => [...prev, { id: generateId(), shares: '', price: '', currency: prev[0]?.currency || 'USD', purchasedDate: '' }]);
    setTimeout(() => {
      lotsScrollRef.current?.scrollTo({ top: lotsScrollRef.current.scrollHeight, behavior: 'smooth' });
    }, 50);
  };

  const handleRemoveLotRow = (id: string) => {
    setNewLots(prev => prev.length > 1 ? prev.filter(lot => lot.id !== id) : prev);
  };

  const handleLotChange = (id: string, field: keyof NewLot, value: string) => {
    if (hasAttemptedSubmit && (field === 'shares' || field === 'price')) {
      setHasAttemptedSubmit(false);
    }
    setNewLots(prev => prev.map(lot => lot.id === id ? { ...lot, [field]: value } : lot));
  };

  const handleAddStock = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    setHasAttemptedSubmit(true);

    // Check for validation errors (skip lot validation for watchlist-only)
    if (!isWatchlistOnly && hasLotValidationError) {
      setAddError('Shares and price must be positive numbers');
      return;
    }

    // Check asset limit for free tier (watchlist stocks don't count)
    if (!isWatchlistOnly && tier === 'free' && portfolioStocks.length >= FREE_TIER_ASSET_LIMIT) {
      posthog.capture('upgrade_modal_shown', {
        trigger: 'asset_limit_reached',
        current_asset_count: portfolioStocks.length,
        tier,
      });
      setIsAddModalOpen(false);
      setIsUpgradeModalOpen(true);
      return;
    }

    if (!newSymbol) {
      setAddError("Please select a stock from the search results.");
      return;
    }

    setIsAddingStock(true);

    try {
      const symbolUpper = newSymbol.toUpperCase();

      if (stocksArray.some(s => s.symbol === symbolUpper)) {
        throw new Error("Stock already in portfolio");
      }

      if (isWatchlistOnly) {
        // Watchlist-only: no lots needed
        const data = await fetchStockData(symbolUpper, { includeProfile: true });

        const newStock: Stock = {
          id: generateId(),
          symbol: symbolUpper,
          name: data.name,
          exchange: data.exchange || selectedExchange,
          currency: data.currency,
          currentPrice: data.currentPrice,
          historicalData: data.history,
          groundingUrls: data.groundingUrls,
          marketCap: data.marketCap,
          nextEarningsDate: data.nextEarningsDate,
          instrumentType: data.instrumentType,
          sector: data.sector,
          lastUpdated: new Date().toISOString(),
          userModifiedAt: new Date().toISOString(),
          isLoading: false,
          lots: [],
          watchlist: true,
        };

        setStocks(prev => ({ ...prev, [newStock.id]: newStock }));

        posthog.capture('stock_added', {
          symbol: symbolUpper,
          exchange: newStock.exchange,
          currency: newStock.currency,
          tier,
          watchlist: true,
        });

        setIsAddModalOpen(false);
        resetAddForm();
        setIsAddingStock(false);
        return;
      }

      // Lots with a purchasedDate can omit price (auto-fetched); lots without need price
      const validLots = newLots.filter(l => l.shares && (l.price || l.purchasedDate));
      if (validLots.length === 0) {
        throw new Error("Please add at least one valid purchase lot.");
      }

      const data = await fetchStockData(symbolUpper, { includeProfile: true });

      const processedLots: StockLot[] = await Promise.all(validLots.map(async (l) => {
        let price = parseFloat(l.price);
        const purchasedDate = l.purchasedDate || undefined;

        // If user provided a date but no price, look up from historical data
        if (purchasedDate && (!l.price || isNaN(price))) {
          // Try local history first, then fall back to API for dates outside 10-year range
          let historicalPrice = findPriceForDate(data.history, purchasedDate);
          if (historicalPrice === null) {
            historicalPrice = await fetchHistoricalPrice(symbolUpper, purchasedDate);
          }
          if (historicalPrice !== null) {
            price = historicalPrice;
          } else {
            throw new Error(`No historical price data found for ${purchasedDate}. Please enter the price manually.`);
          }
        }

        return {
          id: generateId(),
          shares: parseFloat(l.shares),
          price,
          date: purchasedDate ? new Date(purchasedDate + 'T00:00:00').toISOString() : new Date().toISOString(),
          currency: l.currency,
          ...(purchasedDate ? { purchasedDate } : {}),
        };
      }));

      const newStock: Stock = {
        id: generateId(),
        symbol: symbolUpper,
        name: data.name,
        exchange: data.exchange || selectedExchange,
        currency: data.currency,
        currentPrice: data.currentPrice,
        historicalData: data.history,
        groundingUrls: data.groundingUrls,
        marketCap: data.marketCap,

        nextEarningsDate: data.nextEarningsDate,
        instrumentType: data.instrumentType,
        sector: data.sector,
        lastUpdated: new Date().toISOString(),
        userModifiedAt: new Date().toISOString(),
        isLoading: false,
        lots: processedLots
      };

      setStocks(prev => ({ ...prev, [newStock.id]: newStock }));

      posthog.capture('stock_added', {
        symbol: symbolUpper,
        exchange: newStock.exchange,
        currency: newStock.currency,
        tier
      });

      setIsAddModalOpen(false);
      resetAddForm();
    } catch (err) {
      const error = err as Error;
      setAddError(error.message || "Failed to add stock. Please try again.");
    } finally {
      setIsAddingStock(false);
    }
  };

  const handleBulkStocksAdded = useCallback((newStocks: Stock[]) => {
    setStocks(prev => {
      const updated = { ...prev };
      for (const stock of newStocks) {
        updated[stock.id] = stock;
      }
      return updated;
    });
    posthog.capture('stocks_bulk_added', {
      count: newStocks.length,
      symbols: newStocks.map(s => s.symbol),
      tier,
    });
  }, [tier]);

  const handleRefreshStock = async (id: string, symbol: string) => {
    setStocks(prev => ({
      ...prev,
      [id]: { ...prev[id], isLoading: true }
    }));

    try {
      const data = await fetchStockData(symbol);
      setStocks(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          currentPrice: data.currentPrice,
          name: data.name,
          exchange: data.exchange,
          currency: data.currency,
          historicalData: data.history,
          groundingUrls: data.groundingUrls,
          marketCap: data.marketCap,
          nextEarningsDate: data.nextEarningsDate,
          instrumentType: data.instrumentType,
          sector: prev[id].sector ?? data.sector,
          lastUpdated: new Date().toISOString(),
          isLoading: false
        }
      }));

      // Track stock refreshed event
      posthog.capture('stock_refreshed', {
        symbol,
      });
    } catch (err) {
      console.error(err);
      setStocks(prev => ({
        ...prev,
        [id]: { ...prev[id], isLoading: false, error: "Failed to update" }
      }));
    }
  };

  const handleRefreshAll = async () => {
    const promises = stocksArray.map(s => handleRefreshStock(s.id, s.symbol));
    await Promise.all(promises);
  };

  const handleDeleteStock = (id: string) => {
    const stockToDelete = stocks[id];
    if (window.confirm("Are you sure you want to remove this stock from your portfolio?")) {
      setStocks(prev => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });
      if (selectedStockId === id) setSelectedStockId(null);

      // Track stock removed event
      if (stockToDelete) {
        posthog.capture('stock_removed', {
          symbol: stockToDelete.symbol,
          name: stockToDelete.name,
          portfolio_size: stocksArray.length - 1,
          tier,
        });
      }
    }
  };

  const handleAddLot = (stockId: string, shares: number, price: number, date: string, lotCurrency: 'USD' | 'CAD', purchasedDate?: string) => {
    const stock = stocks[stockId];

    // If promoting a watchlist stock, check free-tier limit first
    if (stock?.watchlist && tier === 'free' && portfolioStocks.length >= FREE_TIER_ASSET_LIMIT) {
      posthog.capture('upgrade_modal_shown', {
        trigger: 'watchlist_promotion_limit',
        current_asset_count: portfolioStocks.length,
        tier,
      });
      setSelectedStockId(null);
      setIsUpgradeModalOpen(true);
      return;
    }

    setStocks(prev => ({
      ...prev,
      [stockId]: {
        ...prev[stockId],
        lots: [...prev[stockId].lots, {
          id: generateId(), shares, price, date, currency: lotCurrency,
          ...(purchasedDate ? { purchasedDate } : {}),
        }],
        userModifiedAt: new Date().toISOString(),
        // Promote watchlist stock to portfolio when adding a lot
        ...(prev[stockId].watchlist ? { watchlist: false } : {}),
      }
    }));

    // Track lot added event
    if (stock) {
      posthog.capture('lot_added', {
        symbol: stock.symbol,
        currency: lotCurrency,
        total_lots: stock.lots.length + 1,
      });
    }
  };

  const handleEditLot = (stockId: string, lotId: string, shares: number, price: number, lotCurrency: 'USD' | 'CAD') => {
    const stock = stocks[stockId];
    setStocks(prev => ({
      ...prev,
      [stockId]: {
        ...prev[stockId],
        lots: prev[stockId].lots.map(l => l.id === lotId ? { ...l, shares, price, currency: lotCurrency } : l),
        userModifiedAt: new Date().toISOString(),
      }
    }));

    // Track lot edited event
    if (stock) {
      posthog.capture('lot_edited', {
        symbol: stock.symbol,
        currency: lotCurrency,
      });
    }
  };

  const handleDeleteLot = (stockId: string, lotId: string) => {
    const stock = stocks[stockId];
    const remainingLots = stock.lots.filter(l => l.id !== lotId);
    setStocks(prev => ({
      ...prev,
      [stockId]: {
        ...prev[stockId],
        lots: remainingLots,
        userModifiedAt: new Date().toISOString(),
        // Demote to watchlist when all lots are removed
        ...(remainingLots.length === 0 ? { watchlist: true } : {}),
      }
    }));

    // Track lot deleted event
    if (stock) {
      posthog.capture('lot_deleted', {
        symbol: stock.symbol,
        total_lots: stock.lots.length - 1,
      });
    }
  };

  const selectedStock = selectedStockId ? stocks[selectedStockId] : undefined;

  // Prevent hydration mismatch by not rendering until mounted
  if (!isMounted) {
    return (
      <div className="min-h-screen pb-20">
        <div className="sticky top-14 z-20 backdrop-blur-xl" style={{ backgroundColor: 'color-mix(in srgb, var(--color-bg) 70%, transparent)' }}>
          <div className="container mx-auto flex items-center justify-end p-4">
            <div className="flex gap-2">
              <Button variant="secondary" disabled size="sm">
                <RefreshCw size={16} />
              </Button>
              <Button variant="secondary" disabled size="sm">
                <Upload size={16} />
              </Button>
              <Button disabled size="sm">
                <Plus size={16} />
              </Button>
            </div>
          </div>
        </div>
        <main className="container mx-auto p-4 lg:p-8">
          <div className="mb-8 rounded-2xl p-4 animate-pulse" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="h-12"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Cloud save error banner */}
      {cloudSaveError && (
        <div className="bg-red-500/10 border-b border-red-500/30">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-200">
                  <span className="font-medium">Failed to save to cloud.</span>{' '}
                  Your changes are saved locally but may be lost if you clear your browser data. Please try refreshing the page.
                </p>
              </div>
              <button
                onClick={() => setCloudSaveError(null)}
                className="p-1.5 text-red-400 hover:text-red-300 transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Toolbar */}
      <div style={{ top: headerHeight, backgroundColor: 'var(--color-bg)' }} className="sticky z-20">
        <div className="container mx-auto flex items-center justify-end p-4">
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleRefreshAll} disabled={stocksArray.length === 0} size="sm">
              <RefreshCw size={16} />
            </Button>
            <div className="relative" ref={addMenuRef}>
              <div className="flex">
                <Button
                  onClick={() => { resetAddForm(); setIsAddModalOpen(true); setIsAddMenuOpen(false); }}
                  size="sm"
                  className="rounded-r-none"
                >
                  <Plus size={16} />
                </Button>
                <Button
                  onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
                  size="sm"
                  className="rounded-l-none border-l border-l-white/20 px-1"
                >
                  <ChevronDown size={14} />
                </Button>
              </div>
              {isAddMenuOpen && (
                <div
                  className="absolute right-0 mt-1 w-48 rounded-lg py-1 shadow-lg border"
                  style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
                >
                  <button
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:brightness-125"
                    style={{ color: 'var(--color-text)' }}
                    onClick={() => { resetAddForm(); setIsAddModalOpen(true); setIsAddMenuOpen(false); }}
                  >
                    <Plus size={16} />
                    Add Single Stock
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:brightness-125"
                    style={{ color: 'var(--color-text)' }}
                    onClick={() => { setIsBulkUploadModalOpen(true); setIsAddMenuOpen(false); }}
                  >
                    <Upload size={16} />
                    Bulk Upload
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto p-4 lg:p-8">
        {selectedStock ? (
          <StockDetail
            stock={selectedStock}
            currency={currency}
            exchangeRate={exchangeRate}
            convert={convert}
            onBack={() => setSelectedStockId(null)}
            onAddLot={handleAddLot}
            onEditLot={handleEditLot}
            onDeleteLot={handleDeleteLot}
          />
        ) : (
          <>
            {/* Dashboard Summary — unified strip */}
            <div className="mb-4 sm:mb-6 rounded-2xl p-4" style={{ backgroundColor: 'var(--color-surface)' }}>
              <div className="grid grid-cols-3" style={{ columnGap: '0', display: 'grid' }}>
                <div className="px-3 sm:px-4" style={{ borderRight: '1px solid var(--color-divide)' }}>
                  <span className="text-[10px] sm:text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Value</span>
                  <div className="text-base sm:text-2xl font-bold text-[var(--color-text)] tabular-nums">
                    {currency === 'USD' ? '$' : 'C$'}{formatNumber(convert(summary.totalValue), 0)}
                  </div>
                </div>

                <div className="px-3 sm:px-4" style={{ borderRight: '1px solid var(--color-divide)' }}>
                  <span className="text-[10px] sm:text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Cost</span>
                  <div className="text-base sm:text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                    {currency === 'USD' ? '$' : 'C$'}{formatNumber(convert(summary.totalCost), 0)}
                  </div>
                </div>

                <div className="px-3 sm:px-4">
                  <span className="text-[10px] sm:text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>P/L</span>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0 sm:gap-2">
                    <span className={`text-base sm:text-2xl font-bold tabular-nums ${summary.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {summary.totalProfit >= 0 ? '+' : ''}{currency === 'USD' ? '$' : 'C$'}{formatNumber(Math.abs(convert(summary.totalProfit)), 0)}
                    </span>
                    <span className={`text-[10px] sm:text-sm font-medium ${summary.totalProfit >= 0 ? 'text-green-500/80' : 'text-red-500/80'}`}>
                      ({summary.totalProfitPercent > 0 ? '+' : ''}{formatNumber(summary.totalProfitPercent, 1)}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Portfolio Chart */}
            <PortfolioChart
              stocks={portfolioStocks}
              currency={currency}
              exchangeRate={exchangeRate}
              onSelectStock={(stock) => {
                setSelectedStockId(stock.id);
                posthog.capture('stock_detail_viewed', { symbol: stock.symbol, source: 'treemap' });
              }}
            />

            {/* Stock List Grid */}
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Your Assets</h2>
              {watchlistStocks.length > 0 && (
                <div className="flex gap-1 rounded-full p-0.5" style={{ backgroundColor: 'var(--color-toggle-bg)' }}>
                  <button
                    onClick={() => setStockListView('mixed')}
                    className={`px-2 py-0.5 text-xs font-medium rounded-full transition-all ${stockListView === 'mixed' ? 'shadow' : ''}`}
                    style={stockListView === 'mixed' ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
                    title="Mixed view"
                  >
                    <LayoutGrid size={14} />
                  </button>
                  <button
                    onClick={() => setStockListView('tabbed')}
                    className={`px-2 py-0.5 text-xs font-medium rounded-full transition-all ${stockListView === 'tabbed' ? 'shadow' : ''}`}
                    style={stockListView === 'tabbed' ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
                    title="Tabbed view"
                  >
                    <Columns size={14} />
                  </button>
                </div>
              )}
            </div>

            {stockListView === 'tabbed' && watchlistStocks.length > 0 && (
              <div className="flex gap-1 rounded-full p-0.5 mb-4 w-fit" style={{ backgroundColor: 'var(--color-toggle-bg)' }}>
                <button
                  onClick={() => setActiveListTab('portfolio')}
                  className={`px-3 py-1 text-sm font-medium rounded-full transition-all ${activeListTab === 'portfolio' ? 'shadow' : ''}`}
                  style={activeListTab === 'portfolio' ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
                >
                  Portfolio ({portfolioStocks.length})
                </button>
                <button
                  onClick={() => setActiveListTab('watchlist')}
                  className={`px-3 py-1 text-sm font-medium rounded-full transition-all ${activeListTab === 'watchlist' ? 'shadow' : ''}`}
                  style={activeListTab === 'watchlist' ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
                >
                  Watchlist ({watchlistStocks.length})
                </button>
              </div>
            )}

            {/* Portfolio search */}
            {stocksArray.length > 0 && (
              <div className="relative mb-4">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  size={16}
                  style={{ color: 'var(--color-text-muted)' }}
                />
                <input
                  type="text"
                  value={portfolioSearch}
                  onChange={(e) => setPortfolioSearch(e.target.value)}
                  placeholder="Search your assets by symbol or company name"
                  aria-label="Search your assets"
                  className="w-full rounded-xl p-2.5 pl-10 pr-10 text-sm text-[var(--color-text)] outline-none"
                  style={{ border: '1px solid var(--color-input)', backgroundColor: 'var(--color-surface)' }}
                />
                {portfolioSearch && (
                  <button
                    type="button"
                    onClick={() => setPortfolioSearch('')}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:brightness-125"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}

            {stocksArray.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed py-20 text-center" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}>
                <div className="mb-4 rounded-full p-4" style={{ backgroundColor: 'var(--color-surface-hover)' }}>
                  <TrendingUp size={32} style={{ color: 'var(--color-text-muted)' }} />
                </div>
                <h3 className="text-lg font-medium text-[var(--color-text)]">No stocks tracked yet</h3>
                <p className="mt-1 mb-6 max-w-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Add your first stock to start tracking your portfolio&apos;s performance with AI-powered insights.
                </p>
                <Button onClick={() => { resetAddForm(); setIsAddModalOpen(true); }}>Add Your First Stock</Button>
                <Link
                  href="/learn"
                  className="mt-4 text-sm underline transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  New to investing? Start here →
                </Link>
              </div>
            ) : (
              (() => {
                const visibleStocks = applyPortfolioSearch(
                  stockListView === 'tabbed' && watchlistStocks.length > 0
                    ? (activeListTab === 'portfolio' ? portfolioStocks : watchlistStocks)
                    : stocksArray
                );
                if (visibleStocks.length === 0) {
                  return (
                    <div
                      className="flex flex-col items-center justify-center rounded-2xl border border-dashed py-12 text-center"
                      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface-secondary)' }}
                    >
                      <Search size={24} style={{ color: 'var(--color-text-muted)' }} />
                      <p className="mt-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        No assets match &ldquo;{portfolioSearch}&rdquo;
                      </p>
                      <button
                        type="button"
                        onClick={() => setPortfolioSearch('')}
                        className="mt-3 text-xs underline"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        Clear search
                      </button>
                    </div>
                  );
                }
                return (
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <AnimatePresence mode="popLayout">
                      {visibleStocks.map((stock, i) => (
                        <StockCard
                          key={stock.id}
                          stock={stock}
                          currency={currency}
                          exchangeRate={exchangeRate}
                          convert={convert}
                          onRefresh={handleRefreshStock}
                          onSelect={(s) => {
                            setSelectedStockId(s.id);
                            posthog.capture('stock_detail_viewed', {
                              symbol: s.symbol,
                              name: s.name,
                            });
                          }}
                          onDelete={handleDeleteStock}
                          index={i}
                          isWatchlist={stock.watchlist}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                );
              })()
            )}
          </>
        )}
      </main>

      {/* Add Stock Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => { setIsAddModalOpen(false); resetAddForm(); }} title="Add New Asset">
        <form onSubmit={handleAddStock} className="space-y-4">
          <div className="relative">
            <label className="mb-1 block text-sm font-medium" style={{ color: hasAttemptedSubmit && !newSymbol ? '#f87171' : 'var(--color-text-secondary)' }}>Find Stock{hasAttemptedSubmit && !newSymbol && <span className="text-red-400"> *</span>}</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search by symbol or company (e.g. Apple, TSLA)"
                required
                className="w-full rounded-xl p-2.5 pl-10 text-[var(--color-text)] outline-none"
                style={{ border: `1px solid ${hasAttemptedSubmit && !newSymbol ? '#f87171' : 'var(--color-input)'}`, backgroundColor: 'var(--color-bg)' }}
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2" size={16} style={{ color: 'var(--color-text-muted)' }} />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                   <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: 'var(--color-text-muted)', borderTopColor: 'transparent' }}></div>
                </div>
              )}
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl shadow-xl" style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface-hover)' }}>
                {searchResults.map((result, index) => (
                  <button
                    key={`${result.symbol}-${result.exchange}-${index}`}
                    type="button"
                    onClick={() => handleSelectStock(result)}
                    className="w-full px-4 py-3 text-left flex items-center justify-between last:border-0"
                    style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
                  >
                    <div>
                      <span className="font-bold text-[var(--color-text)]">{result.symbol}</span>
                      <span className="ml-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{result.name}</span>
                    </div>
                    <span className="rounded px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}>
                      {result.exchange}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Watchlist toggle */}
          <div className="flex items-center justify-between rounded-xl p-3" style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2">
              <Eye size={16} style={{ color: 'var(--color-text-muted)' }} />
              <div>
                <span className="text-sm font-medium text-[var(--color-text)]">Watchlist only</span>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Track price without adding to portfolio</p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isWatchlistOnly}
              onClick={() => setIsWatchlistOnly(!isWatchlistOnly)}
              className="relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors"
              style={{ backgroundColor: isWatchlistOnly ? 'var(--color-accent)' : 'var(--color-surface-hover)' }}
            >
              <span className="inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform" style={{ marginTop: '2px', marginLeft: isWatchlistOnly ? '22px' : '2px' }} />
            </button>
          </div>

          {!isWatchlistOnly && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Purchase Details</label>
              {selectedExchange && (
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Exchange: {selectedExchange}</span>
              )}
            </div>

            <div ref={lotsScrollRef} className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {newLots.map((lot) => {
                const sharesNum = parseFloat(lot.shares);
                const priceNum = parseFloat(lot.price);
                const isSharesInvalid = lot.shares !== '' && (isNaN(sharesNum) || sharesNum <= 0);
                const isPriceInvalid = lot.price !== '' && (isNaN(priceNum) || priceNum <= 0);
                const isSharesMissing = hasAttemptedSubmit && lot.shares === '';
                const isPriceMissing = hasAttemptedSubmit && !lot.purchasedDate && lot.price === '';

                return (
                <div key={lot.id} className="flex flex-col gap-2 p-2 rounded-xl overflow-hidden" style={{ backgroundColor: 'color-mix(in srgb, var(--color-bg) 50%, transparent)', border: '1px solid var(--color-border)' }}>
                  <div className="grid grid-cols-10 gap-2">
                    <div className="col-span-3">
                      <label className="text-[10px] block mb-1" style={{ color: isSharesMissing ? '#f87171' : 'var(--color-text-muted)' }}>Shares{isSharesMissing && <span className="text-red-400"> *</span>}</label>
                      <input
                        type="number" step="any" required placeholder="Qty"
                        className={`w-full rounded p-1.5 text-sm text-[var(--color-text)] outline-none ${isSharesInvalid || isSharesMissing ? 'border-red-500' : ''}`}
                        style={{ backgroundColor: 'var(--color-surface-hover)', border: `1px solid ${isSharesInvalid || isSharesMissing ? '#f87171' : 'var(--color-input)'}` }}
                        value={lot.shares}
                        onChange={(e) => handleLotChange(lot.id, 'shares', e.target.value)}
                      />
                    </div>
                    <div className="col-span-4">
                      <label className="text-[10px] block mb-1" style={{ color: isPriceMissing ? '#f87171' : 'var(--color-text-muted)' }}>{lot.purchasedDate ? 'Price (auto)' : 'Avg Price'}{isPriceMissing && <span className="text-red-400"> *</span>}</label>
                      <input
                        type="number" step="any" required={!lot.purchasedDate} placeholder={lot.purchasedDate ? 'Auto' : '0.00'}
                        className={`w-full rounded p-1.5 text-sm text-[var(--color-text)] outline-none ${isPriceInvalid || isPriceMissing ? 'border-red-500' : ''}`}
                        style={{ backgroundColor: 'var(--color-surface-hover)', border: `1px solid ${isPriceInvalid || isPriceMissing ? '#f87171' : 'var(--color-input)'}` }}
                        value={lot.price}
                        onChange={(e) => handleLotChange(lot.id, 'price', e.target.value)}
                      />
                    </div>
                    <div className="col-span-3">
                       <label className="text-[10px] block mb-1" style={{ color: 'var(--color-text-muted)' }}>Currency</label>
                       <select
                        value={lot.currency}
                        onChange={(e) => handleLotChange(lot.id, 'currency', e.target.value)}
                        className="w-full rounded p-1.5 text-sm text-[var(--color-text)] outline-none"
                        style={{ backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-input)' }}
                       >
                         <option value="USD">USD</option>
                         <option value="CAD">CAD</option>
                       </select>
                    </div>
                  </div>
                  <div className="overflow-hidden">
                    <label className="text-[10px] block mb-1" style={{ color: 'var(--color-text-muted)' }}>Purchase Date <span style={{ opacity: 0.5 }}>(optional — auto-fills price)</span></label>
                    <div className="flex gap-1.5">
                      <input
                        type="date"
                        max={new Date().toISOString().split('T')[0]}
                        className="min-w-0 flex-1 rounded p-1.5 text-sm text-[var(--color-text)] outline-none"
                        style={{ backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-input)' }}
                        value={lot.purchasedDate}
                        onChange={(e) => handleLotChange(lot.id, 'purchasedDate', e.target.value)}
                      />
                      {lot.purchasedDate && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewLots(prev => prev.map(l => l.id === lot.id ? { ...l, purchasedDate: '', price: '' } : l));
                          }}
                          className="rounded px-2 text-xs shrink-0"
                          style={{ color: 'var(--color-text-muted)', border: '1px solid var(--color-input)', backgroundColor: 'var(--color-surface-hover)' }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  {newLots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveLotRow(lot.id)}
                      className="self-end text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  )}
                </div>
              );
              })}
            </div>

            <Button type="button" variant="ghost" size="sm" onClick={handleAddLotRow} className="w-full" style={{ border: '1px dashed var(--color-border)' }}>
              <Plus size={14} className="mr-1" /> Add Another Bucket
            </Button>
          </div>
          )}

          {!isWatchlistOnly && hasLotValidationError && (
            <p className="text-red-400 text-sm">Shares and price must be positive numbers</p>
          )}

          {addError && (
            <div className="rounded-xl bg-red-900/50 p-3 text-sm text-red-200 border border-red-800">
              {addError}
            </div>
          )}

          <div className="pt-2 flex flex-col items-center gap-3">
            <Button type="submit" isLoading={isAddingStock} disabled={!isWatchlistOnly && hasLotValidationError}>
              {isWatchlistOnly ? 'Add to Watchlist' : 'Add Asset'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setIsAddModalOpen(false); resetAddForm(); setIsBulkUploadModalOpen(true); }}
            >
              <Upload size={14} className="mr-1" />
              Add multiple stocks at once
            </Button>
          </div>
        </form>
      </Modal>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        existingSymbols={stocksArray.map(s => s.symbol.toUpperCase())}
        onStocksAdded={handleBulkStocksAdded}
        tier={tier}
        portfolioCount={portfolioStocks.length}
        assetLimit={FREE_TIER_ASSET_LIMIT}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />
    </div>
  );
}
